export const INCLUDE = 'Include'
export const EXCLUDE = 'Exclude'

/**
 * Controller for the vd-autocompleteFilter component.
 *
 * @class AutocompleteFilterController
 */
export default class AutocompleteFilterController {
  constructor($scope, $rootScope, $attrs, $element, $document, $timeout) {
    'ngInject'
    this._$attrs = $attrs
    this._$scope = $scope
    this._$rootScope = $rootScope
    this._$element = $element
    this._$document = $document
    this._$timeout = $timeout
  }

  $onInit() {
    this.isInitialized = false
    this.showSearchTwo = false

    // focus events
    const filterId = (Math.random() * 10e8).toFixed(0).toString()
    this.focusFilterOne = `vd-acfilter:${filterId}:focus1`
    this.focusFilterTwo = `vd-acfilter:${filterId}:focus2`

    // Assign default search select by labels
    this.searchOneSelectBy = INCLUDE
    this.searchTwoSelectBy = EXCLUDE

    // Re-define $isEmpty
    this.ngModelCtrl.$isEmpty = () => {
      const viewValue = this.ngModelCtrl.$viewValue
      const hasNoIncluded =
        !viewValue || !viewValue.included || viewValue.included.length === 0
      const hasNoExcluded =
        !viewValue || !viewValue.excluded || viewValue.excluded.length === 0

      return Boolean(hasNoIncluded && hasNoExcluded)
    }

    // Re-define $render
    this.ngModelCtrl.$render = () => this._renderModel()

    // Set a watch to run render if any changes to the model occur outside of the component
    this._$scope.$watch(
      () => this.ngModelCtrl.$viewValue,
      () => {
        this.ngModelCtrl.$render()
      },
      true
    )
  }

  $postLink() {
    this._$attrs.$addClass('vd-autocomplete-filter')

    this._$element.on('focus', event => {
      if (event.target.matches('.vd-autocomplete-filter-focusable')) {
        this._onFocus()
      }
    })
    this._$element.on('blur', event => {
      if (event.target.matches('.vd-autocomplete-filter-focusable')) {
        this._onBlur()
      }
    })
  }

  /**
   * Toggle the select by state, will change the select by state and update the models appropriately
   *
   * @method toggleSelectBy
   */
  toggleSelectBy() {
    if (this.searchOneSelectBy === INCLUDE) {
      this.searchTwoSelectBy = EXCLUDE
    } else {
      this.searchTwoSelectBy = INCLUDE
    }

    this.updateSearchModel({ quiet: !this.searchOne.length })
  }

  /**
   * Toggle the second filter input
   * When hiding the second filter input we will update the second filter model to be empty
   *
   * @method toggleSearchTwo
   */
  toggleSearchTwo() {
    this.showSearchTwo = !this.showSearchTwo

    if (this.showSearchTwo) {
      this._$timeout(() =>
        this._$rootScope.$emit(this.focusFilterTwo, { isFocusable: true })
      )
    } else {
      this._$timeout(() =>
        this._$rootScope.$emit(this.focusFilterOne, { isFocusable: true })
      )
      this.searchTwo.length = 0
      this.updateSearchModel()
    }
  }

  /**
   * Update search models
   *
   * @method updateSearchModel
   *
   * @param {Object} options
   *
   *        @param {Boolean} options.quiet
   *               Do not run $setViewValue if quite is set to true, run otherwise
   */
  updateSearchModel(options = {}) {
    const included =
      this.searchOneSelectBy === INCLUDE ? this.searchOne : this.searchTwo
    const excluded =
      this.searchOneSelectBy === INCLUDE ? this.searchTwo : this.searchOne

    if (options.quiet) {
      this.ngModelCtrl.$viewValue = { included, excluded }
    } else {
      this.ngModelCtrl.$setViewValue({ included, excluded })
    }
  }

  // ============================== Private Methods =============================== //

  /**
   * Set the model to touched on focus
   *
   * @private
   *
   * @method _onFocus
   */
  _onFocus() {
    this._$scope.$applyAsync(() => this.ngModelCtrl.$setTouched())
  }

  /**
   * Set the model to dirty on blur
   *
   * @private
   *
   * @method _onBlur
   */
  _onBlur() {
    this._$timeout(() => {
      const activeElement = this._$document[0].activeElement
      if (
        !this._$element[0].contains(activeElement) &&
        this._$element[0].contains('.vd-autocomplete-filter-focusable.ng-dirty')
      ) {
        this.ngModelCtrl.$setDirty()
      }
    })
  }

  /**
   * Update the internal representation of the model to the current $viewValue of ng-model.
   *
   * @private
   *
   * @method _renderModel
   */
  _renderModel() {
    if (!this.ngModelCtrl.$viewValue) {
      this.searchOne = []
      this.searchTwo = []

      this.updateSearchModel({ quiet: true })

      this.isInitialized = true
      return
    }

    const model = this.ngModelCtrl.$viewValue

    if (!model.included) {
      model.included = []
    }
    if (!model.excluded) {
      model.excluded = []
    }

    const hasIncluded = Boolean(model.included.length)
    const hasExcluded = Boolean(model.excluded.length)

    // When only included is available set that as the model for searchOne
    if (!hasIncluded && hasExcluded && !this.isInitialized) {
      this.searchOneSelectBy = EXCLUDE
      this.searchTwoSelectBy = INCLUDE
    }

    // When both included and excluded are available set respective models to searchOne and searchTwo
    if (hasIncluded && hasExcluded) {
      this.showSearchTwo = true
    }

    if (this.searchOneSelectBy === INCLUDE) {
      this.searchOne = model.included
      this.searchTwo = model.excluded
    } else {
      this.searchOne = model.excluded
      this.searchTwo = model.included
    }

    this.isInitialized = true
  }
}
